import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  Chip,
  TextField,
} from "@mui/material"
import { Norm } from "graphql/types"
import { SyntheticEvent } from "react"
import { getNormName } from "../../utils/getNormName"

type AutocompleteMultiProps<T> = {
  options: T[]
  value: T[]
  onChange: (
    event: SyntheticEvent<Element, Event>,
    value: T[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T> | undefined
  ) => void
  label: string
  isEnum?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
} & Omit<
  AutocompleteProps<T, true, false, false>,
  "options" | "value" | "onChange" | "renderInput" | "renderOption" | "renderTags"
>

export const AutocompleteMulti = <T extends { id?: string; name?: string } | string>(
  props: AutocompleteMultiProps<T>
) => {
  const {
    options,
    value,
    onChange,
    label,
    isEnum,
    required,
    error,
    helperText,
    getOptionLabel: getOptionLabelProp,
    ...restProps
  } = props

  const getOptionLabel = (option: T) => {
    if (isEnum) return option as string
    if (getOptionLabelProp) {
      // Use the provided getOptionLabel if available
      return (getOptionLabelProp as (option: T) => string)(option)
    }

    // Check if it's a Norm object and use getNormName
    if (isNorm(option)) {
      return getNormName(option as any as Norm)
    }

    return (option as { name: string }).name || ""
  }

  // Helper function to check if an option is a Norm
  const isNorm = (option: T): boolean => {
    const obj = option as any
    return obj && obj.__typename === "Norm"
  }

  return (
    <Autocomplete
      size="small"
      multiple
      options={options}
      getOptionLabel={getOptionLabel}
      filterSelectedOptions
      value={value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={isEnum ? (option as string) : (option as { id: string }).id}>
          {getOptionLabel(option)}
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={index}
            label={getOptionLabel(option)}
            size="small"
          />
        ))
      }
      onChange={onChange}
      {...restProps}
    />
  )
}
